import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import useApiFetch from '@/hooks/useApiFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import useApiSubmit from '@/hooks/useApiSubmit';
import financeSubscriptionsService from '@/services/financeSubscriptions';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import { FxSubscription, FxVendor } from '@/types/api/finance';
import FinanceXSubscriptionsComponent from './FinanceXSubscriptions.component';

const FinanceXSubscriptionsContainer = function (): JSX.Element {
  const queryClient = useQueryClient();
  const orgId = getActiveOrgId();
  const [showForm, setShowForm] = useState(false);
  const [editingSub, setEditingSub] = useState<FxSubscription | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subsResponse = useApiFetch<{ data: FxSubscription[] }, void>(
    [FetchApiIds.FX_SUBSCRIPTIONS, orgId],
    async () => financeSubscriptionsService.getAll({ page: 1, take: 100 }),
    { retry: false }
  );

  const vendorsResponse = useApiFetch<{ data: FxVendor[] }, void>(
    [FetchApiIds.FX_VENDORS, orgId],
    async () => financeVendorsService.getAll({ page: 1, take: 200 }),
    { retry: false }
  );

  const subscriptions = useMemo(() => (subsResponse.data as any)?.data ?? [], [subsResponse.data]);

  const vendors = useMemo(() => (vendorsResponse.data as any)?.data ?? [], [vendorsResponse.data]);

  const cancelMutation = useApiSubmit<FxSubscription, any>(
    (payload) => financeSubscriptionsService.cancel(payload.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_SUBSCRIPTIONS);
      },
    }
  );

  const deleteMutation = useApiSubmit<any, any>(
    async (payload) => {
      await financeSubscriptionsService.delete(payload.id);
      return {} as any;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_SUBSCRIPTIONS);
      },
    }
  );

  const handleSubmit = useCallback(
    async (data: any) => {
      setIsSubmitting(true);
      setError(null);
      try {
        let { vendorId } = data;

        // Create new vendor first if needed
        if (vendorId === '__new__' && data.newVendorName) {
          const nameLower = data.newVendorName.trim().toLowerCase();
          const existing = vendors.find(
            (v: FxVendor) =>
              v.name.toLowerCase() === nameLower || v.displayName?.toLowerCase() === nameLower
          );
          if (existing) {
            vendorId = existing.id;
          } else {
            const created = await financeVendorsService.create({ name: data.newVendorName.trim() });
            vendorId = (created.data as any).id;
            queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
          }
        } else if (vendorId === '__new__') {
          setError('Please enter a vendor name.');
          setIsSubmitting(false);
          return;
        }

        // Check for duplicate subscription (same vendor + same name, only for create)
        if (!editingSub) {
          const duplicate = subscriptions.find(
            (s: FxSubscription) =>
              s.vendorId === vendorId &&
              s.name.toLowerCase() === data.name.trim().toLowerCase() &&
              (s.status === 'active' || s.status === 'trial')
          );
          if (duplicate) {
            setError(`A subscription "${duplicate.name}" already exists for this vendor.`);
            setIsSubmitting(false);
            return;
          }
        }

        // Build clean payload for the subscription service
        const subPayload: Record<string, unknown> = {
          vendorId,
          name: data.name,
          amount: Number(data.amount),
          billingCycle: data.billingCycle,
          startDate: data.startDate,
        };
        if (data.currency) subPayload.currency = data.currency;
        if (data.nextBilling) subPayload.nextBilling = data.nextBilling;
        if (data.seats) subPayload.seats = Number(data.seats);
        if (data.status) subPayload.status = data.status;
        if (data.owner) subPayload.owner = data.owner;
        if (data.notes) subPayload.notes = data.notes;

        if (editingSub) {
          await financeSubscriptionsService.update(editingSub.id, subPayload as any);
        } else {
          await financeSubscriptionsService.create(subPayload as any);
        }

        queryClient.invalidateQueries(FetchApiIds.FX_SUBSCRIPTIONS);
        setShowForm(false);
        setEditingSub(null);
      } catch (err: any) {
        setError(err.message || 'Failed to save subscription.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [editingSub, vendors, queryClient]
  );

  const handleEdit = useCallback((sub: FxSubscription) => {
    setEditingSub(sub);
    setShowForm(true);
  }, []);

  const handleCancel = useCallback(
    (sub: FxSubscription) => {
      cancelMutation.mutate({ id: sub.id });
    },
    [cancelMutation]
  );

  const handleDelete = useCallback(
    (sub: FxSubscription) => {
      deleteMutation.mutate({ id: sub.id });
    },
    [deleteMutation]
  );

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingSub(null);
  }, []);

  return (
    <FinanceXSubscriptionsComponent
      subscriptions={subscriptions}
      vendors={vendors}
      isLoading={subsResponse.isLoading}
      showForm={showForm}
      editingSub={editingSub}
      onNewSub={() => setShowForm(true)}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onDelete={handleDelete}
      onSubmit={handleSubmit}
      onCloseForm={handleCloseForm}
      isSubmitting={isSubmitting}
      error={error}
    />
  );
};

export default FinanceXSubscriptionsContainer;
